import { FC, useState } from 'react';
import { Paper, Typography, Chip, Grid, Box } from '@material-ui/core';
// import CheckCircleIcon from '@material-ui/icons/CheckCircle';
// import CancelIcon from '@material-ui/icons/Cancel';
import { toFormatDateTime } from 'utils';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';

interface AppLogin {
  provider: string;
  app_name: string;
  last_login: string | null;
}

interface LastDeviceLogin {
  login_time: string;
  ip_address: string;
  city: string;
  region: string;
  country: string;
  device: string;
  os: string;
  browser: string;
}

export interface RiskData {
  last_login: string | null;
  easyidentity_mfa_enabled: boolean;
  provider_mfa_enabled: boolean;
  active_findings_count: number;
  active_findings: Finding[];
  app_logins: AppLogin[];
  last_device_login: LastDeviceLogin | null;
}

// interface Props {
//   risk: RiskData;
// }

interface ConnectedApp {
  app_name: string;
  provider: string;
  external_id: string;
  synced_date?: string;
}

interface Finding {
  id: number;
  risk_type: string;
  severity: string;
  last_login: string | null;
  days_inactive: number;
  mfa_enabled: boolean;
  connected_apps: ConnectedApp[];
  remediation_steps: string[];
  created_at: string;
}

interface Props {
  risk: RiskData;
}

const EndUserSecurityInsights: FC<Props> = ({ risk }) => {
  // const mfaEnabled = risk.easyidentity_mfa_enabled || risk.provider_mfa_enabled;
  const [openFindings, setOpenFindings] = useState(false);
  return (
    <>
      <Paper
        elevation={0}
        style={{
          padding: '16px 20px',
          marginBottom: '16px',
          background: 'linear-gradient(135deg, #fff8f0 0%, #fff3e0 100%)',
          border: '1px solid #ffe0b2',
          borderRadius: 8,
        }}
      >
        <Typography variant='subtitle1' style={{ fontWeight: 700, marginBottom: 8, color: '#bf360c' }}>
          🔐 My Security Insights
        </Typography>
        <Box style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {risk.last_login && (
            <Grid item>
              <Typography
                variant='caption'
                color='textSecondary'
                style={{ fontWeight: 600, textTransform: 'uppercase' }}
              >
                Last Login
              </Typography>
              <Typography variant='body2' style={{ fontWeight: 600, marginTop: 4 }}>
                {toFormatDateTime(risk.last_login)}
              </Typography>
            </Grid>
          )}

          <Grid item>
            <Typography variant='caption' color='textSecondary' style={{ fontWeight: 600, textTransform: 'uppercase' }}>
              Active Findings
            </Typography>
            <div style={{ marginTop: 4 }}>
              {/* <Chip
              size="small"
              label={risk.active_findings_count === 0 ? 'No Issues' : `${risk.active_findings_count} Issue(s)`}
              style={{
                backgroundColor: risk.active_findings_count === 0 ? '#4caf50' : '#f44336',
                color: '#fff',
                fontWeight: 600,
              }}
            /> */}

              <Chip
                clickable={risk.active_findings_count > 0}
                onClick={() => {
                  if (risk.active_findings_count > 0) {
                    setOpenFindings(true);
                  }
                }}
                size='small'
                label={risk.active_findings_count === 0 ? 'No Issues' : `${risk.active_findings_count} Issue(s)`}
                style={{
                  cursor: risk.active_findings_count > 0 ? 'pointer' : 'default',
                  backgroundColor: risk.active_findings_count === 0 ? '#4caf50' : '#f44336',
                  color: '#fff',
                  fontWeight: 600,
                }}
              />
            </div>
          </Grid>

          {/* Last Device */}
          {risk.last_device_login && (
            <Grid item>
              <Typography
                variant='caption'
                color='textSecondary'
                style={{ fontWeight: 600, textTransform: 'uppercase' }}
              >
                Last Device
              </Typography>
              <Typography variant='body2' style={{ marginTop: 4 }}>
                {risk.last_device_login.browser} on {risk.last_device_login.os} ({risk.last_device_login.device})
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {risk.last_device_login.city}, {risk.last_device_login.country} · {risk.last_device_login.ip_address}
              </Typography>
            </Grid>
          )}

          {risk.app_logins &&
            risk.app_logins.map((app, i) => (
              <Grid item key={i}>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  style={{ fontWeight: 600, textTransform: 'uppercase' }}
                >
                  Connected App
                </Typography>
                {/* <Paper elevation={0} style={{ padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 6, minWidth: 160 }}> */}
                <Typography variant='body2' style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                  {app.app_name || app.provider}
                </Typography>
                <Typography variant='caption' color='textSecondary'>
                  {app.last_login ? toFormatDateTime(app.last_login) : 'Never logged in'}
                </Typography>
                {/* </Paper> */}
              </Grid>
            ))}
        </Box>
      </Paper>
      <Dialog open={openFindings} onClose={() => setOpenFindings(false)} maxWidth='md' fullWidth>
        <DialogTitle>Security Findings</DialogTitle>

        <DialogContent dividers>
          {risk.active_findings.map((item) => (
            <Paper
              key={item.id}
              elevation={0}
              style={{
                padding: 16,
                marginBottom: 16,
                border: '1px solid #ddd',
              }}
            >
              <Typography variant='h6'>{item.risk_type.replace('_', ' ').toUpperCase()}</Typography>

              <Typography color='textSecondary'>
                Severity: <b>{item.severity}</b>
              </Typography>

              <Typography color='textSecondary'>
                Last Login: {item.last_login ? toFormatDateTime(item.last_login) : 'Never'}
              </Typography>

              {item.days_inactive >= 0 && <Typography>Days Inactive: {item.days_inactive}</Typography>}

              <Typography style={{ marginTop: 10 }}>Connected Apps</Typography>

              {item.connected_apps.map((app, index) => (
                <Typography key={index} variant='body2'>
                  • {app.app_name}
                </Typography>
              ))}

              <Typography style={{ marginTop: 10, fontWeight: 600 }}>Remediation Steps</Typography>

              <ul>
                {item.remediation_steps.map((step, index) => (
                  <li key={index}>
                    <Typography variant='body2'>{step}</Typography>
                  </li>
                ))}
              </ul>
            </Paper>
          ))}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenFindings(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EndUserSecurityInsights;
